import React, { useState, useEffect } from "react"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import UserApi from "../../services/users";
import DashboardHeader from '../../components/DashboardHeader';

function UserPage(props) {
    const { userId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [user, setUser] = useState({});
    const [loading, setLoading] = useState(true);
    const [tenantId] = useState('1'); // Default tenant ID - this should come from context or props

    // Get filter state from navigation
    const filters = location.state?.filters || {};

    const getBackUrl = () => {
        let url = '/users';
        const params = new URLSearchParams();
        
        if (filters.search) params.append('search', filters.search);
        if (filters.selectedNetwork) params.append('network', filters.selectedNetwork);
        if (filters.activeLetter && filters.activeLetter !== 'all') params.append('letter', filters.activeLetter);
        
        const queryString = params.toString();
        return queryString ? `${url}?${queryString}` : url;
    };

    useEffect(() => {
        const fetchUser = async () => {
            // Check if user data was passed through navigation state
            if (location.state && location.state.user) {
                setUser(location.state.user);
                setLoading(false);
                return;
            }

            // If no user data in state, try to fetch directly
            const userApi = new UserApi(props.token);
            try {
                const foundUser = await userApi.getById(userId);
                setUser(foundUser);
            } catch (error) {
                console.error('Error fetching user:', error);
                // Show error message instead of redirecting
                setUser({
                    id: userId,
                    first_name: 'User',
                    last_name: 'Not Available',
                    email: 'N/A',
                    phone: 'N/A',
                    status: 'unknown',
                    error: 'Individual user data not available. Please use the users list to view user details.'
                });
            } finally {
                setLoading(false);
            }
        };

        if (userId) {
            fetchUser();
        }
    }, [userId, props.token, tenantId, navigate, location.state]);

    if (loading) {
        return (
            <div className='dashboard-content'>
                <div className='dashboard-content-container'>
                    <div className='dashboard-content-header'>
                        <h2>Loading...</h2>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='dashboard-content'>
            <DashboardHeader
                btnText="Back to Users" 
                link={getBackUrl()} />
            <div className='dashboard-content-container'>
                <div className='dashboard-content-header'>
                    <h2>User Details</h2>
                </div>
                <div className="card">
                    <div className="card-body">
                        <div className="row">
                            <div className="col-md-6">
                                <h5>Personal Information</h5>
                                <p><strong>Name:</strong> {user.first_name} {user.last_name}</p>
                                <p><strong>Email:</strong> {user.email}</p>
                                <p><strong>Phone:</strong> {user.phone || 'Not provided'}</p>
                                <p><strong>Status:</strong> 
                                    <span className={`badge ms-2 ${user.status ? 'bg-success' : 'bg-secondary'}`}>
                                        {user.status ? 'Active' : 'Not Active'}
                                    </span>
                                </p>
                                <p><strong>Superuser:</strong> 
                                    <span className={`badge ms-2 ${user.is_superuser ? 'bg-warning' : 'bg-light text-dark'}`}>
                                        {user.is_superuser ? 'Yes' : 'No'}
                                    </span>
                                </p>
                            </div>
                            <div className="col-md-6">
                                <h5>Account Information</h5>
                                <p><strong>Created:</strong> {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown'}</p>
                                <p><strong>Last Updated:</strong> {user.updated_at ? new Date(user.updated_at).toLocaleDateString() : 'Unknown'}</p>
                            </div>
                        </div>
                        
                        {/* Network Membership Information */}
                        {(user.networks && user.networks.length > 0) && (
                            <div className="row mt-3">
                                <div className="col-12">
                                    <h5>Network Memberships</h5>
                                    {user.networks.map((network, index) => (
                                        <div key={index} className="card mb-3">
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <p><strong>Network:</strong> {network.tenant_name}</p>
                                                        <p><strong>Network Role:</strong> {network.role_name}</p>
                                                        <p><strong>Membership Status:</strong> 
                                                            <span className={`badge ms-2 ${
                                                                network.status === 'approved' ? 'bg-success' : 
                                                                network.status === 'pending' ? 'bg-warning' : 
                                                                network.status === 'rejected' ? 'bg-danger' : 'bg-secondary'
                                                            }`}>
                                                                {network.status}
                                                            </span>
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        
                        {/* Error Message */}
                        {user.error && (
                            <div className="row mt-3">
                                <div className="col-12">
                                    <div className="alert alert-warning">
                                        {user.error}
                                    </div>
                                </div>
                            </div>
                        )}
                        {user.profile_image && (
                            <div className="row mt-3">
                                <div className="col-12">
                                    <h5>Profile Image</h5>
                                    <img 
                                        src={user.profile_image.url} 
                                        alt="Profile" 
                                        className="img-thumbnail" 
                                        style={{maxWidth: '200px', maxHeight: '200px'}}
                                    />
                                </div>
                            </div>
                        )}
                        
                        {/* Action Buttons */}
                        <div className="row mt-4">
                            <div className="col-12">
                                <button 
                                    className="btn btn-primary"
                                    onClick={() => navigate(`/user/${userId}/edit`, { 
                                        state: { 
                                            user: user,
                                            filters: filters
                                        } 
                                    })}
                                >
                                    Edit User
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default UserPage
